/* eslint-disable react-refresh/only-export-components */

import {
  useMemo,
  useContext,
  createContext,
  useState,
  FC,
  PropsWithChildren,
} from 'react';

import { createContextualCan } from '@casl/react';
import { defineAbility } from '@casl/ability';
import { STATUS } from 'utils/constants';

export const FeatureFlagContext = createContext({ state: {} });
export const useFeatureFlagContext = () =>
  useContext<{
    state: {
      [key: string]: any;
    };
    dispatch?: React.Dispatch<React.SetStateAction<object>>;
  }>(FeatureFlagContext);

export function useValidateFeatureFlag(
  featureFlags: any,
  acl: any,
  status = true,
) {
  if (
    featureFlags &&
    acl &&
    !acl
      .map((key: string) => {
        if (!featureFlags[key]) return false;

        if (status && featureFlags[key].status === STATUS.INACTIVE)
          return false;

        return true;
      })
      .filter((res: any) => Boolean(res)).length
  )
    return false;

  return true;
}

export function ValidateFeatureFlag(acl: any) {
  const {
    state: { featureFlags },
  } = useFeatureFlagContext();

  return useValidateFeatureFlag(featureFlags, acl);
}

export const AbilityContext = createContext<any>({});
export const Can = createContextualCan(AbilityContext.Consumer);

export const useAbilityContext = () => useContext(AbilityContext);

export const Ability: React.FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { featureFlags },
  } = useFeatureFlagContext();

  const ability = defineAbility((can) => {
    if (featureFlags) {
      Object.keys(featureFlags).forEach((key) =>
        [featureFlags[key].key, featureFlags[key].status].forEach(
          (featureFlag) => {
            can(featureFlag, key);
          },
        ),
      );
    }
  });

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};

export const useCan = (featureFlag: string) => {
  const ability = useContext(AbilityContext);

  return ability.can(STATUS.ACTIVE, featureFlag);
};

const FeatureFlag: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useState({});

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <FeatureFlagContext.Provider value={value}>
      <Ability>{children}</Ability>
    </FeatureFlagContext.Provider>
  );
};

export default FeatureFlag;
